<template>
  <div class="container">
    <!-- Search and Filter Section -->
    <div class="row text-light pb-3 pt-2 mt-1 align-items-end">

      <!-- Search Input -->
      <div class="col-md-4 mb-2">
        <form
          class="search-form search-form-theme2 w-100"
          @submit.prevent
        >
          <img
            src="@/assets/images/skeleton/search.png"
            class="fas fa-search search-icon"
            alt="search"
          >
          <input
            v-model="filter.keyword"
            type="text"
            placeholder="Search in logs"
            name="Search"
            class="search-input"
          >
        </form>
      </div>

      <!-- Filter by Experiment Name -->
      <div class="col-md-4 mb-2">
        <label class="filter-label">Experiment Name</label>
        <v-select
          v-model="filter.experiment_id"
          :options="experimentsLookups"
          :reduce="(val) => val.id"
          label="name"
          placeholder="All Experiments"
          class="institution-select"
          :clearable="true"
        />
      </div>

      <!-- Filter by Institution -->
      <div class="col-md-4 mb-2">
        <label class="filter-label">Institution</label>
        <v-select
          v-model="filter.institution_id"
          :options="institutionLookups"
          :reduce="(val) => val.id"
          label="name"
          placeholder="All Institutions"
          class="institution-select"
          :clearable="true"
        />
      </div>

      <!-- Date Range: From -->
      <div class="col-md-3 mb-2">
        <label class="filter-label">Date From</label>
        <flat-pickr
          v-model="filter.date_from"
          :class="['form-control logs-date-picker', { 'date-error-border': dateError }]"
          placeholder="Date From"
          :config="dateFromConfig"
        />
      </div>

      <!-- Date Range: To -->
      <div class="col-md-3 mb-2">
        <label class="filter-label">Date To</label>
        <flat-pickr
          v-model="filter.date_to"
          :class="['form-control logs-date-picker', { 'date-error-border': dateError }]"
          placeholder="Date To"
          :config="dateToConfig"
        />
        <!-- Inline date error -->
        <transition name="fade-error">
          <small
            v-if="dateError"
            class="date-error-msg"
          >
            <span>&#9888;</span> "Date From" can't be after "Date To"
          </small>
        </transition>
      </div>

      <!-- Clear Filters Button -->
      <div class="col-md-2 mb-2 d-flex align-items-end">
        <button
          class="clear-filters-btn"
          @click="clearFilters"
        >
          <span>Clear Filters</span>
        </button>
      </div>

    </div>

    <!-- Loading Spinner -->
    <center v-if="isLoading">
      <b-spinner
        variant="primary"
        label="Loading"
      />
    </center>

    <!-- Users Table -->
    <b-table
      v-if="!isLoading && logs.data && logs.data.length > 0"
      id="drag-index"
      :items="logs.data"
      :fields="fields"
      striped
      responsive
      class="users"
      no-local-sorting
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @sort-changed="onSortChanged"
    >
      <template #cell(username)="data">
        <b-link
          v-if="data.item.user_id"
          :to="{ name: 'pages_profile', params: { user_id: data.item.user_id } }"
          class="text-primary font-weight-bold"
        >
          {{ data.item.username }}
        </b-link>
        <span v-else>{{ data.item.username || "N/A" }}</span>
      </template>

      <template #cell(experiment_name)="data">
        <b-link
          v-if="data.item.experiment_id"
          :to="{ name: 'experiment_details', params: { id: data.item.experiment_id } }"
          class="text-primary font-weight-bold"
        >
          {{ data.item.experiment_name }}
        </b-link>
        <span v-else>{{ data.item.experiment_name || "N/A" }}</span>
      </template>

      <template #cell(progress)="data">
        <div v-if="data.item.progress != null">
          {{ formatPercent(data.item.progress) }}
        </div>
      </template>

      <template #cell(ended_at)="data">
        <div>{{ data.item.ended_at || "N/A" }}</div>
      </template>
      <template #cell(duration_formatted)="data">
        <div>{{ data.item.duration_formatted || "N/A" }}</div>
      </template>

      <!-- <template #cell(institution)="data">
        <div>
          {{
            data.item && data.item.institution
              ? data.item.institution.name
              : "N/A"
          }}
        </div>
      </template>

      <template #cell(roles)="data">
        <b-badge
          pill
          variant="light-primary"
          v-for="value in  data.item.roles"
        >
          {{ value }}
        </b-badge>
      </template> -->

      <!-- <template #cell(Actions)="data">
        <div class="actions d-flex align-items-center">

        </div>
      </template> -->
    </b-table>

    <!-- No Results Message -->

    <div
      v-if="!isLoading && (!logs.data || logs.data.length === 0)"
      class="text-center text-light py-5"
    >
      <p>No Logs found</p>
    </div>

    <!-- Pagination & Per Page -->
    <b-row class="mt-2 w-100 align-items-center" v-if="logs.meta">
      <b-col md="4" class="text-light d-flex align-items-center" style="font-size: 0.95rem;">
        <span v-if="logs.meta.total > 0" class="d-flex align-items-center" style="margin-left: 2px;">
          Showing 
          <b-form-select
            v-model="filter.per_page"
            :options="[10, 25, 50, 100]"
            class="custom-per-page mx-1"
            @change="onPageChange(1)"
          />
        </span>
      </b-col>
      <b-col md="4" class="d-flex justify-content-center">
        <b-pagination
          v-model="logs.meta.current_page"
          :total-rows="logs.meta.total"
          :per-page="logs.meta.per_page"
          @change="onPageChange"
          class="mb-0"
        />
      </b-col>
      <b-col md="4"></b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BButton,
  BFormCheckbox,
  BCard,
  BRow,
  BCol,
  BBadge,
  BAvatar,
  BCardText,
  BLink,
  BSpinner,
  BPagination,
  VBTooltip,
  BFormSelect,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { mapGetters } from 'vuex'

export default {
  components: {
    BTable,
    BButton,
    BFormCheckbox,
    BCard,
    BRow,
    BCol,
    BBadge,
    BAvatar,
    BCardText,
    BLink,
    BSpinner,
    BPagination,
    vSelect,
    flatPickr,
    BFormSelect,
  },

  computed: {
    ...mapGetters({
      logs: 'logs/items',
      institutionLookups: 'institutions/lookups',
      experimentsLookups: 'experiments/experimentsLookups',
    }),

    dateError() {
      if (this.filter.date_from && this.filter.date_to) {
        return new Date(this.filter.date_from) > new Date(this.filter.date_to)
      }
      return false
    },

    dateFromConfig() {
      return {
        dateFormat: 'Y-m-d',
        disableMobile: true,
        maxDate: this.filter.date_to || null,
      }
    },

    dateToConfig() {
      return {
        dateFormat: 'Y-m-d',
        disableMobile: true,
        minDate: this.filter.date_from || null,
      }
    },
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      logsDetails: [],
      isLoading: false,
      sortBy: 'started_at',
      sortDesc: true,
      filter: {
        institution_id: this.$route.params.institution_id || null,
        experiment_id: this.$route.query.experiment_id ? Number(this.$route.query.experiment_id) : null,
        keyword: null,
        date_from: null,
        date_to: null,
        per_page: 10,
      },
      fields: [
        { key: 'username', label: 'User Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'experiment_name', label: 'Experiment Name', sortable: true },
        { key: 'progress', label: 'Progress', sortable: true },
        { key: 'started_at', label: 'Started At', sortable: true },
        { key: 'ended_at', label: 'Ended At', sortable: true },
        { key: 'duration_formatted', label: 'Duration', sortable: true },
        // { key: "Actions", label: "Actions", thStyle: { width: "10%" } },
      ],
    }
  },

  watch: {
    '$route.query.experiment_id': function (newVal) {
      this.filter.experiment_id = newVal ? Number(newVal) : null
    },
    'filter.keyword': function (newVal) {
      // Debounce search
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.init()
      }, 500)
    },
    'filter.institution_id': function (newVal) {
      this.init()
    },
    'filter.experiment_id': function (newVal) {
      this.init()
    },
    'filter.date_from': function (newVal) {
      if (!this.dateError) this.init()
    },
    'filter.date_to': function (newVal) {
      if (!this.dateError) this.init()
    },
  },

  mounted() {
    this.init()
    this.$store.dispatch('institutions/institutionLookups')
    this.$store.dispatch('experiments/experimentsLookups')
  },

  methods: {
    init() {
      this.isLoading = true
      this.$store
        .dispatch('logs/listLogs', {
          ...this.filter,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
        })
        .then(response => {
          this.isLoading = false
        })
        .catch(e => {
          this.isLoading = false
          this.$swal({
            icon: 'error',
            title: `<h4>Error fetching logs details: ${e.message}</h4>`,
            showConfirmButton: true,
            confirmButtonColor: '#7367f0',
            allowOutsideClick: true,
          })
        })
    },
    formatPercent(val) {
      const num = Number(val)
      if (isNaN(num)) return '-'
      return `${+num.toFixed(2)}%`
    },
    onPageChange(page) {
      this.$store.dispatch('logs/listLogs', {
        page,
        ...this.filter,
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
      })
    },

    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.init()
    },

    clearFilters() {
      this.filter.experiment_id = null
      this.filter.institution_id = this.$route.params.institution_id || null
      this.filter.keyword = null
      this.filter.date_from = null
      this.filter.date_to = null
      this.sortBy = 'started_at'
      this.sortDesc = true
      if (this.$route.query.experiment_id) {
        this.$router.replace({ query: { ...this.$route.query, experiment_id: undefined } }).catch(() => {})
      }
    },

  },

}
</script>

<style scoped lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "~flatpickr/dist/themes/dark.css";
.search-form {
  position: relative;
  z-index: 1000;
  width: 100%;
  background-color: transparent;
  border: 1px solid #ffffff75;
  border-left: none;
  border-right: none;
  border-radius: 15px;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.4);
  padding: 10px 20px 10px 45px;
  transition: box-shadow 0.3s ease;
}

.search-form-theme2 {
  position: relative;
  z-index: 1000;
  width: 100%;
  border-left: none;
  border-right: none;
  padding: 10px 20px 10px 45px;
  transition: box-shadow 0.3s ease;
  background-color: #00000040;
  border: 1px solid #ffffff75;
  border-radius: 15px;
  box-shadow: inset 0 2px 5px rgb(0 0 0 / 65%);
  border-top: unset;
}

.search-form:focus-within {
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
}

.search-form input[type="text"],
.search-form input[type="search"] {
  width: 100%;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 16px;
  outline: none;
}

.search-form input::placeholder {
  color: #ffffffa8;
}

.search-form .search-icon {
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  color: #bbb;
  font-size: 16px;
  cursor: pointer;
}

.institution-select {
  ::v-deep .vs__dropdown-toggle {
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 5px;
  }

  ::v-deep .vs__search,
  ::v-deep .vs__search:focus {
    color: #fff;
  }

  ::v-deep .vs__selected {
    color: #fff;
  }

  ::v-deep .vs__dropdown-menu {
    background: #2c2c2c;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  ::v-deep .vs__dropdown-option {
    color: #fff;

    &:hover {
      background: rgba(115, 103, 240, 0.3);
    }
  }
}

.user-table-container {
  max-width: 90%;
  border-radius: 14px;
  overflow: hidden;
  backdrop-filter: blur(18px);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.table-header {
  background-color: rgba(35, 34, 34, 0.15);
  color: #fff;
  font-size: 0.95rem;
}

.user-table-body {
  max-height: 500px;
  overflow-y: auto;
}

.actions {
  gap: 8px;
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.filter-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 5px;
  text-transform: uppercase;
}

.logs-date-picker {
  background-color: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 8px !important;
  padding: 5px 10px !important;
  min-height: 38px !important;
  color: #fff !important;
  font-size: 0.875rem;
  box-shadow: none !important;
  transition: border-color 0.2s;
  cursor: pointer;
  height: auto !important;
  width: 100%;
  display: flex !important;
  align-items: center;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
  }

  &:focus {
    border-color: rgba(115, 103, 240, 0.7) !important;
    box-shadow: none !important;
    outline: none !important;
  }
}

.clear-filters-btn {
  width: 100%;
  padding: 10px 16px;
  background: rgba(115, 103, 240, 0.15);
  border: 1px solid rgba(115, 103, 240, 0.4);
  border-radius: 15px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.1s;
  letter-spacing: 0.03em;

  &:hover {
    background: rgba(115, 103, 240, 0.3);
    border-color: rgba(115, 103, 240, 0.7);
    color: #fff;
  }

  &:active {
    transform: scale(0.97);
  }
}

/* Date validation error styles */
.date-error-border {
  border-color: rgba(234, 84, 85, 0.85) !important;
  box-shadow: 0 0 0 2px rgba(234, 84, 85, 0.25), inset 0 2px 5px rgb(0 0 0 / 65%) !important;
}

.date-error-msg {
  display: block;
  margin-top: 5px;
  color: #ea5455;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.01em;

  span {
    margin-right: 3px;
  }
}

/* Fade-slide transition for the error message */
.fade-error-enter-active,
.fade-error-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-error-enter,
.fade-error-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
